import { useEffect, useRef, useState } from 'react';
import layout from '../dictionaries/layout.js';
import { useAtomValue } from 'jotai';
import { clientAtom, teamsAtom, gamePhaseAtom, hostAtom, turnAtom, deviceAtom, languageAtom } from '../GlobalState.jsx';
import { Center, Text3D } from '@react-three/drei';
import Piece from './Piece.jsx';
import { formatName, getNameFont } from '../logicHelpers/helpers.js';
import YootMesh from '../meshes/YootMesh.jsx';
import { useParams } from 'wouter';
import Star from '../meshes/Stars/Star.jsx';
import YouStars from "./YouStars.jsx";
import * as THREE from 'three';
import { tileType } from '../gameLogic/rules.js';
import translations from '../dictionaries/translations.js';

export default function Team({ position=[0,0,0], scale=1, team }) {
  const device = useAtomValue(deviceAtom)
  const teams = useAtomValue(teamsAtom)
  const gamePhase = useAtomValue(gamePhaseAtom);
  const host = useAtomValue(hostAtom);
  const turn = useAtomValue(turnAtom)
  const client = useAtomValue(clientAtom);
  const params = useParams();
  const language = useAtomValue(languageAtom)
  const teamName = teams[team].name || (team === 0 ? translations.game.teamNames.rockets[language] : translations.game.teamNames.ufos[language])

  // Need to accept "key" to use it in an map
  function EmptyPiece({ position }) {
    return <mesh position={position}>
      <sphereGeometry args={[0.2, 32, 16]} />
    </mesh>
  }

  function ScoredPiece({ position }) {
    return <mesh position={position}>
      <Star color={team == 0 ? "red" : "green"} scale={0.3}/>
    </mesh>
  }

  function HomePieces({position, scale=1}) {
    let space = layout[device].game[`team${team}`].pieces.space;
    let positionStartX = layout[device].game[`team${team}`].pieces.positionStartX;
    let positionStartY = layout[device].game[`team${team}`].pieces.positionStartY;
    let positionStartZ = layout[device].game[`team${team}`].pieces.positionStartZ;

    return (
      <group position={position} scale={scale}>
        {
          teams[team].pieces.map((value, index) =>
            tileType(value.tile) === "onBoard" ? <Star 
              position={[
                positionStartX + index * space,
                positionStartY,
                positionStartZ,
              ]}
              key={index}
              scale={0.3}
              color='grey'
            /> : 
            tileType(value.tile) === "scored" ? <ScoredPiece
              position={[
                positionStartX + index * space,
                positionStartY,
                positionStartZ,
              ]}
              key={index}
            /> : <Piece
              position={[
                positionStartX + index * space,
                positionStartY,
                positionStartZ,
              ]}
              rotation={layout[device].game[`team${team}`].pieces.rotation}
              scale={layout[device].game[`team${team}`].pieces.scale}
              tile={-1}
              team={team}
              id={value.id}
              key={index}
              animation={null}
            />
          )
        }
      </group>
    );
  }

  function RollDisplay() {
    // Handle 0 because it is coerced to 'null' as a string
    let rollText;
    if (teams[team].pregameRoll === null) {
      rollText = '';
    } else if (teams[team].pregameRoll === 0) {
      rollText = '&#10007' // not working
    } else {
      rollText = teams[team].pregameRoll.toString()
    }

    return <group position={layout[device].game[`team${team}`].pregameRoll.position}>
      { teams[team].pregameRoll === 0 ? <group>
        <Text3D
          font="/fonts/Luckiest Guy_Regular.json"
          position={[-1, 0.025, 0.15]}
          rotation={[-Math.PI/2, 0, 0]}
          size={layout[device].game[`team${team}`].pregameRoll.size}
          height={layout[device].game[`team${team}`].pregameRoll.height}
        >
          {`roll:`}
          <meshStandardMaterial color='yellow'/>
        </Text3D>
        <Text3D
          font="/fonts/Luckiest Guy_Regular.json"
          position={[0.35, 0.025, 0.15]}
          rotation={[-Math.PI/2, 0, 0]}
          size={layout[device].game[`team${team}`].pregameRoll.size}
          height={layout[device].game[`team${team}`].pregameRoll.height}
        >
          {'X'}
          <meshStandardMaterial color='grey'/>
        </Text3D>
      </group> : <Text3D
        font="/fonts/Luckiest Guy_Regular.json"
        position={[-1, 0.025, 0.15]}
        rotation={[-Math.PI/2, 0, 0]}
        size={layout[device].game[`team${team}`].pregameRoll.size}
        height={layout[device].game[`team${team}`].pregameRoll.height}
      >
        {`roll: ${rollText}`}
        <meshStandardMaterial color='yellow'/>
      </Text3D> }
    </group>
  }

  function PlayerIds() {

    const fontUrl = language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json";


    function AIPlayingText() {
      const language = useAtomValue(languageAtom);
      const [text, setText] = useState(`${translations.game.aiPlaying[language]} . . .`)
      const [count, setCount] = useState(1)

      useEffect(() => {

        const interval = setInterval(() => {
          setText(_text => {
            let newText = `${translations.game.aiPlaying[language]}`
            for (let i = 0; i < count; i++) {
              newText += ' .'
            }
            setCount(count => {
              let newCount = count+1
              if (count === 3) {
                newCount = 1
              }
              return newCount
            })
            return newText
          })
        }, 800)

        return () => {
          clearInterval(interval)
        }
      })
      
      return text
    }

    function PlayerIndicators({ value, index, nameWidth, position }) {
      // YOU, host, current player
      const isYourClient = value.name === client.name
      const isHostClient = host.socketId === value.socketId
      const isCurrentPlayer = turn.team === team && turn.players[turn.team] === index

      // case 0: it's your client, client host
      // case 1: it's your client
      // case 2: it's not your client, client is host
      // case 3: it's not your client
      // add isCurrentPlayer at the end

      if (isYourClient && isHostClient) {
        return <group name={`player-${index}-team-${team}-indicators`} position={position}>
          <YouStars 
          name='you-indicator' 
          team={team} 
          scale={0.4} 
          position={[nameWidth-0.4, 0, 0]} 
          rotation={[Math.PI/2, 0, 0]}/>
          <Star 
          name='host-indicator' 
          scale={0.23} 
          rotation={[Math.PI/2, 0, 0]} 
          position={[nameWidth+0.1, 0, 0]}/>
          { isCurrentPlayer && <group 
          name='current-player-indicator' 
          position={[nameWidth+0.4, 0, 0.05]}
          scale={0.8}>
            <YootMesh rotation={[0, Math.PI/2, 0]} scale={0.04}/>
            <YootMesh rotation={[0, Math.PI/2, 0]} scale={0.04} position={[0.1, 0, 0]}/>
            <YootMesh rotation={[0, Math.PI/2, 0]} scale={0.04} position={[0.2, 0, 0]}/>
            <YootMesh rotation={[0, Math.PI/2, 0]} scale={0.04} position={[0.3, 0, 0]}/>
          </group> }  
        </group>
      } else if (isYourClient && !isHostClient) {
        return <group name={`player-${index}-team-${team}-indicators`} position={position}>
          <YouStars 
          name='you-indicator' 
          team={team} 
          scale={0.4} 
          position={[nameWidth-0.4, 0, 0]} 
          rotation={[Math.PI/2, 0, 0]}/>
          { isCurrentPlayer && <group 
          name='current-player-indicator' 
          position={[nameWidth, 0, 0.05]}
          scale={0.8}>
            <YootMesh rotation={[0, Math.PI/2, 0]} scale={0.04}/>
            <YootMesh rotation={[0, Math.PI/2, 0]} scale={0.04} position={[0.1, 0, 0]}/>
            <YootMesh rotation={[0, Math.PI/2, 0]} scale={0.04} position={[0.2, 0, 0]}/>
            <YootMesh rotation={[0, Math.PI/2, 0]} scale={0.04} position={[0.3, 0, 0]}/>
          </group> }  
        </group>
      } else if (!isYourClient && isHostClient) {
        return <group name={`player-${index}-team-${team}-indicators`} position={position}>
          <Star 
          name='host-indicator' 
          scale={0.23} 
          rotation={[Math.PI/2, 0, 0]} 
          position={[nameWidth-0.3, 0, 0]}/>
          { isCurrentPlayer && <group 
          name='current-player-indicator' 
          position={[nameWidth, 0, 0.05]}
          scale={0.8}>
            <YootMesh rotation={[0, Math.PI/2, 0]} scale={0.04}/>
            <YootMesh rotation={[0, Math.PI/2, 0]} scale={0.04} position={[0.1, 0, 0]}/>
            <YootMesh rotation={[0, Math.PI/2, 0]} scale={0.04} position={[0.2, 0, 0]}/>
            <YootMesh rotation={[0, Math.PI/2, 0]} scale={0.04} position={[0.3, 0, 0]}/>
          </group> }  
        </group>
      }  else if (!isYourClient && !isHostClient) {
        return <group name={`player-${index}-team-${team}-indicators`} position={position}>
          { isCurrentPlayer && <group 
          name='current-player-indicator' 
          position={[nameWidth-0.5, 0, 0.05]}
          scale={0.8}>
            <YootMesh rotation={[0, Math.PI/2, 0]} scale={0.04}/>
            <YootMesh rotation={[0, Math.PI/2, 0]} scale={0.04} position={[0.1, 0, 0]}/>
            <YootMesh rotation={[0, Math.PI/2, 0]} scale={0.04} position={[0.2, 0, 0]}/>
            <YootMesh rotation={[0, Math.PI/2, 0]} scale={0.04} position={[0.3, 0, 0]}/>
          </group> }  
        </group>
      } 
    }

    function Player({value, index, position}) {
      const nameRef = useRef()
      const [nameWidth, setNameWidth] = useState(0)
      return <group name={`player-${index}-team-${team}`} position={position}>
        <Center right ref={nameRef} onCentered={({ boundingBox }) => {
          const size = boundingBox.getSize(new THREE.Vector3());
          setNameWidth(size.x);
        }}>
          <Text3D
            font={getNameFont(value.name, fontUrl)}
            size={layout[device].game[`team${team}`].names.size}
            height={layout[device].game[`team${team}`].names.height}
          >
            { value.type === 'human' ? formatName(value.name, layout[device].game[`team${team}`].names.maxLength)
            + (value.status === 'away' ? ' (away)' : '') : teams[turn.team].players[turn.players[turn.team]].name === value.name ? AIPlayingText() : formatName(value.name, layout[device].game[`team${team}`].names.maxLength) }
            <meshStandardMaterial color={ value.roomId === params.id.toUpperCase() && value.connectedToRoom ? team === 0 ? 'red' : 'turquoise' : 'gray' }/>
          </Text3D>
        </Center>
        <PlayerIndicators value={value} index={index} nameWidth={nameWidth} position={[0.6, 0, 0]} />
      </group>
    }

    return <group
      position={layout[device].game[`team${team}`].names.position}
      rotation={layout[device].game[`team${team}`].names.rotation}
    >
      {teams[team].players.map((value, index) => (
        index < 4 && <group key={index}>
          <Player value={value} index={index} position={[0, 0, -1.2 * index + 0.3]}/>
        </group>
      ))}
    </group>
  }

  return <group
    position={position}
    scale={scale}
  >
    <group position={layout[device].game[`team${team}`].title.position} rotation={layout[device].game[`team${team}`].title.rotation}>
      <Text3D
        font={getNameFont(teamName, language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json")}
        position={[0, 0, 0]}
        size={layout[device].game[`team${team}`].title.size}
        height={layout[device].game[`team${team}`].title.height}
      >
        {teamName}
        <meshStandardMaterial color={team === 0 ? 'red' : 'turquoise'} />
      </Text3D>
    </group>
    {gamePhase === 'game' && teams[team].enhancedYut && <group
      name={`blessed-throw-indicator-team-${team}`}
      position={[
        layout[device].game[`team${team}`].title.position[0] + (team === 0 ? 2.5 : 1.7),
        layout[device].game[`team${team}`].title.position[1] + 0.05,
        layout[device].game[`team${team}`].title.position[2]
      ]}
      rotation={layout[device].game[`team${team}`].title.rotation}
    >
      <mesh position={[0, 0.2, 0]}><coneGeometry args={[0.18, 0.38, 16]}/><meshStandardMaterial color='yellow' emissive='yellow' emissiveIntensity={0.5}/></mesh>
      <mesh position={[0, -0.08, 0]}><cylinderGeometry args={[0.07, 0.07, 0.35, 16]}/><meshStandardMaterial color='yellow' emissive='yellow' emissiveIntensity={0.5}/></mesh>
    </group>}
    {/* join button */}
    { gamePhase === "pregame" && <RollDisplay/> }
    {/* pieces */}
    <HomePieces 
    position={layout[device].game[`team${team}`].pieces.position} 
    team={team} 
    scale={layout[device].game[`team${team}`].pieces.sectionScale}
    />
    {/* player ids */}
    <PlayerIds/>
    {/* copy link */}
  </group>
}